import { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  Check,
  X,
  Trash2,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  Ban,
  ShieldOff,
  Loader2,
} from "lucide-react";
import { useAdminCircleApplications } from "@/hooks/useAdminCircleApplications";
import { useAdminCircleBans } from "@/hooks/useAdminCircleBans";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const APPLICATION_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-500 border-red-500/20",
};

const APPLICATION_STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock className="w-3 h-3" />,
  approved: <CheckCircle className="w-3 h-3" />,
  rejected: <XCircle className="w-3 h-3" />,
};

interface UnifiedUser {
  id: string;
  user_id: string;
  user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
  application?: {
    id: string;
    status: string;
    message: string | null;
    created_at: string;
    rejection_reason: string | null;
  };
  ban?: {
    id: string;
    banned_at: string;
    reason: string | null;
    no_show_count: number | null;
    auto_ban: boolean | null;
  };
}

export function CircleUsersSection() {
  const {
    applications,
    isLoading: applicationsLoading,
    approveApplication,
    rejectApplication,
    deleteApplication,
  } = useAdminCircleApplications();
  const { bans, isLoading: bansLoading, unbanUser, banUser } = useAdminCircleBans();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [unbanConfirm, setUnbanConfirm] = useState<string | null>(null);
  const [banDialog, setBanDialog] = useState<{ userId: string; userName: string } | null>(null);
  const [banReason, setBanReason] = useState("");

  // Merge applications and bans into unified user list
  const unifiedUsers = useMemo(() => {
    const userMap = new Map<string, UnifiedUser>();

    // Add all applications
    applications.forEach((app) => {
      const userId = app.user_id;
      userMap.set(userId, {
        id: app.id,
        user_id: userId,
        user: app.user,
        application: {
          id: app.id,
          status: app.status,
          message: app.message,
          created_at: app.created_at,
          rejection_reason: app.rejection_reason,
        },
      });
    });

    // Merge bans
    bans.forEach((ban) => {
      const userId = ban.user_id;
      const existing = userMap.get(userId);
      if (existing) {
        existing.ban = {
          id: ban.id,
          banned_at: ban.banned_at,
          reason: ban.reason,
          no_show_count: ban.no_show_count,
          auto_ban: ban.auto_ban,
        };
      } else {
        userMap.set(userId, {
          id: ban.id,
          user_id: userId,
          user: ban.user,
          ban: {
            id: ban.id,
            banned_at: ban.banned_at,
            reason: ban.reason,
            no_show_count: ban.no_show_count,
            auto_ban: ban.auto_ban,
          },
        });
      }
    });

    return Array.from(userMap.values());
  }, [applications, bans]);

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "?";
  };

  const filteredUsers = unifiedUsers.filter((user) => {
    const matchesSearch =
      user.user?.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.user?.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.application?.message?.toLowerCase().includes(search.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === "pending") {
      matchesStatus = user.application?.status === "pending";
    } else if (statusFilter === "approved") {
      matchesStatus = user.application?.status === "approved";
    } else if (statusFilter === "rejected") {
      matchesStatus = user.application?.status === "rejected";
    } else if (statusFilter === "banned") {
      matchesStatus = !!user.ban;
    }

    return matchesSearch && matchesStatus;
  });

  const handleReject = () => {
    if (rejectDialog) {
      rejectApplication.mutate({ applicationId: rejectDialog, reason: rejectReason || undefined });
      setRejectDialog(null);
      setRejectReason("");
    }
  };

  const handleBan = () => {
    if (banDialog) {
      banUser.mutate({ userId: banDialog.userId, reason: banReason || undefined });
      setBanDialog(null);
      setBanReason("");
    }
  };

  if (applicationsLoading || bansLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="pending">Pending Application</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {unifiedUsers.length === 0 ? "No users yet" : "No users match your filters"}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Application</TableHead>
                <TableHead>Ban</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.user_id} className="h-9">
                  <TableCell className="py-1">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user.user?.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {getInitials(user.user?.first_name, user.user?.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium whitespace-nowrap">
                        {user.user?.first_name} {user.user?.last_name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-1 text-muted-foreground whitespace-nowrap">
                    {user.user?.email || "—"}
                  </TableCell>
                  <TableCell className="py-1">
                    {user.application ? (
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`gap-1 text-xs ${APPLICATION_STATUS_COLORS[user.application.status]}`}
                        >
                          {APPLICATION_STATUS_ICONS[user.application.status]}
                          {user.application.status}
                        </Badge>
                        {user.application.message && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground truncate max-w-[120px] cursor-help">
                                  "{user.application.message.slice(0, 20)}..."
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                <p>{user.application.message}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1">
                    {user.ban ? (
                      <div className="flex items-center gap-1">
                        <Badge variant="destructive" className="text-xs gap-1">
                          <Ban className="w-3 h-3" />
                          Banned
                        </Badge>
                        {user.ban.auto_ban && (
                          <Badge variant="secondary" className="text-xs">Auto</Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1">
                    <div className="flex items-center justify-center gap-1">
                      {/* Application Actions */}
                      {user.application?.status === "pending" && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10"
                            onClick={() => approveApplication.mutate(user.application!.id)}
                            disabled={approveApplication.isPending}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setRejectDialog(user.application!.id)}
                            disabled={rejectApplication.isPending}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}

                      {/* Ban/Unban Actions */}
                      {user.ban ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => setUnbanConfirm(user.user_id)}
                          disabled={unbanUser.isPending}
                        >
                          <ShieldOff className="h-3.5 w-3.5" />
                        </Button>
                      ) : user.application?.status === "approved" ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10"
                          onClick={() =>
                            setBanDialog({
                              userId: user.user_id,
                              userName: `${user.user?.first_name || ""} ${user.user?.last_name || ""}`.trim() || "User",
                            })
                          }
                          disabled={banUser.isPending}
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}

                      {/* Delete Application */}
                      {user.application && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteConfirm(user.application!.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(open) => !open && setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Optionally provide a reason for rejection. This will be visible to the user.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={!!banDialog} onOpenChange={(open) => !open && setBanDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban User from Circle</DialogTitle>
            <DialogDescription>
              Ban {banDialog?.userName} from creating or joining Circle events.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ban-reason">Reason (optional)</Label>
            <Textarea
              id="ban-reason"
              placeholder="Why is this user being banned?"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialog(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBan} disabled={banUser.isPending}>
              {banUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ban User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteConfirm) {
                  deleteApplication.mutate(deleteConfirm);
                  setDeleteConfirm(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unban Confirmation */}
      <AlertDialog open={!!unbanConfirm} onOpenChange={() => setUnbanConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unban User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unban this user? They will be able to create and join Circle events again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (unbanConfirm) {
                  unbanUser.mutate(unbanConfirm);
                  setUnbanConfirm(null);
                }
              }}
            >
              Unban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
